"use client";

import React, { ReactElement, useState, useEffect, useTransition, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FaSortAmountDown } from "react-icons/fa";
import { Search, Loader2 } from "lucide-react";
import type { CouponWithStoreData } from "@/types/couponsWithStoresData";
import CouponModal from "@/components/coupon_popup";
import CouponRevealButton from "@/components/CouponRevealButton";
import SearchableStoreSelect from "@/components/SearchableStoreSelect";
import type { CategoryData } from "@/types/category";
import { incrementCouponUsesAction } from "@/actions/couponActions";
import { openAffiliateLink } from "@/lib/openAffiliateLink";
import { AdHalfpage, AdRectangle1, AdRectangle2 } from "@/components/ads/GptAd";

interface AllCouponsPageProps {
  initialCoupons: CouponWithStoreData[];
  totalCoupons: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  categories?: CategoryData[];
  couponId?: string;
  category?: string;
  initialCouponType?: "deal" | "coupon";
  initialVerified?: boolean;
  initialFreeShipping?: boolean;
  initialSortBy?: "relevance" | "newest" | "discount_desc" | "discount_asc" | "most_used";
  initialSearch?: string;
  initialStores?: { _id: string; name: string }[];
  expiredCoupons?: CouponWithStoreData[];
}

function extractPercent(discount?: string | null): number {
  if (!discount) return 0;
  const m = /([0-9]{1,3})%/.exec(discount);
  return m ? Number(m[1]) : 0;
}

function pluralize(n: number, s: string, p = s + "s") {
  return `${n} ${n === 1 ? s : p}`;
}

function formatDiscountDisplay(discount?: string | null): { type: "deal" | "percentage" | "text"; value: string; secondLine?: string } {
  if (!discount || discount.trim() === "") {
    return { type: "deal", value: "Deal" };
  }
  const percentMatch = discount.match(/(\d+)%/);
  const dollarMatch = discount.match(/\$?\s?(\d+)\$?/);
  if (percentMatch) {
    return { type: "percentage", value: `${percentMatch[1]}%` };
  }
  if (dollarMatch) {
    return { type: "percentage", value: `$${dollarMatch[1]}` };
  }
  const parts = discount.split(' ');
  if (parts.length > 1) {
    return { type: "text", value: parts[0], secondLine: parts.slice(1).join(' ') };
  }
  return { type: "text", value: discount };
}

// Skeleton Loading Component
function CouponSkeleton() {
  return (
    <div className="flex flex-col border border-gray-200 rounded-xl bg-white shadow-md overflow-hidden animate-pulse">
      <div className="flex flex-row items-stretch">
        <div className="flex flex-col items-center justify-center min-w-[90px] md:min-w-[120px] p-3 md:p-6 bg-gray-100">
          <div className="h-8 w-16 bg-gray-300 rounded mb-2"></div>
          <div className="h-4 w-12 bg-gray-300 rounded"></div>
        </div>
        <div className="flex-1 p-3 md:p-6">
          <div className="h-4 w-16 bg-gray-300 rounded mb-3"></div>
          <div className="h-6 w-3/4 bg-gray-300 rounded mb-3"></div>
          <div className="flex gap-2">
            <div className="h-5 w-20 bg-gray-300 rounded"></div>
            <div className="h-5 w-24 bg-gray-300 rounded"></div>
          </div>
        </div>
        <div className="hidden md:flex flex-col items-center justify-center min-w-[120px] md:min-w-[200px] p-3 md:p-6 border-l border-gray-100">
          <div className="h-11 w-36 bg-gray-300 rounded-full"></div>
          <div className="h-4 w-24 bg-gray-300 rounded mt-3"></div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Component ─────────────────────────── */
export default function AllCouponsPage({
  initialCoupons,
  totalCoupons,
  currentPage: initialPage,
  totalPages: initialTotalPages,
  limit: initialLimit,
  categories = [],
  couponId,
  category: initialCategory,
  initialCouponType,
  initialVerified,
  initialFreeShipping,
  initialSortBy = "relevance",
  initialSearch,
  initialStores = [],
  expiredCoupons = [],
}: AllCouponsPageProps): ReactElement {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [localSearch, setLocalSearch] = useState(initialSearch || "");

  // State for current data (updated via URL params)
  const [coupons, setCoupons] = useState<CouponWithStoreData[]>(initialCoupons);
  const [total, setTotal] = useState(totalCoupons);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [limit, setLimit] = useState(initialLimit);

  const currentPageFromUrl = Number.parseInt(searchParams.get("page") || String(initialPage), 10);
  const effectivePage = Number.isFinite(currentPageFromUrl) && currentPageFromUrl > 0 ? currentPageFromUrl : 1;

  // Filter state from URL
  // Fix: Check tab parameter first, then couponType for proper tab indicator
  const tabParam = searchParams.get("tab");
  const couponTypeParam = searchParams.get("couponType");
  const activeTab = tabParam === "promo" ? "promo" 
    : tabParam === "deal" ? "deal" 
    : couponTypeParam === "coupon" ? "promo"
    : couponTypeParam === "deal" ? "deal"
    : "all";
  
  // Get categories from URL - support both 'categories' (plural) and 'category' (singular/legacy)
  const categoriesParam = searchParams.get("categories");
  const categoryParam = searchParams.get("category");
  const selectedCategories = categoriesParam 
    ? categoriesParam.split(",").filter(Boolean)
    : categoryParam 
    ? [categoryParam] // Convert single category to array
    : [];
  
  const verified = searchParams.get("verified") === "true";
  const freeShipping = searchParams.get("freeShipping") === "true";
  const sortBy = (searchParams.get("sortBy") as any) || initialSortBy;
  const search = searchParams.get("search") || "";
  const selectedStoreId = searchParams.get("storeId") || "";

  // Convert legacy 'category' parameter to 'categories' format in URL
  useEffect(() => {
    if (categoryParam && !categoriesParam) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("category");
      params.set("categories", categoryParam);
      startTransition(() => router.replace(`/coupons?${params.toString()}`));
    }
  }, [categoryParam, categoriesParam, searchParams, router, startTransition]);

  // Modal
  const [selectedCoupon, setSelectedCoupon] = useState<CouponWithStoreData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Expanded coupon details
  const [expandedCouponId, setExpandedCouponId] = useState<string | null>(null);
  const [expandedExpiredCoupons, setExpandedExpiredCoupons] = useState<string[]>([]);

  // Update URL params helper
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    params.set("page", "1");
    startTransition(() => {
      router.push(`/coupons?${params.toString()}`);
    });
  };

  // The server route already renders the filtered coupons list from the URL params.
  // Doing a second client-side fetch here duplicated the query and made filter changes feel slow.

  useEffect(() => {
    setCoupons(initialCoupons);
    setTotal(totalCoupons);
    setPage(effectivePage);
    setTotalPages(initialTotalPages);
    setLimit(initialLimit);
  }, [initialCoupons, totalCoupons, effectivePage, initialTotalPages, initialLimit]);

  // Sync local search with URL search
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // ───────── URL couponId support ─────────
  useEffect(() => {
    if (couponId) {
      // Search both active and expired so modal opens for any couponId
      const allCoupons = [...coupons, ...(expiredCoupons || [])];
      const coupon = allCoupons.find((c) => c._id === couponId);
      if (coupon) {
        setSelectedCoupon(coupon);
        setIsModalOpen(true);
      }
    }
  }, [couponId, coupons, expiredCoupons]);

  // ───────── Open coupon modal without forcing a redirect
  const handleOpenCouponNewTab = (coupon: CouponWithStoreData) => {
    if (coupon.couponUrl) {
      openAffiliateLink(coupon.couponUrl, { couponId: coupon._id });
    } else if (coupon.store?.slug && typeof window !== "undefined") {
      window.location.assign(`/stores/${coupon.store.slug}`);
      return;
    }
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("couponId", coupon._id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    }

    void incrementCouponUsesAction(coupon._id).catch((error) => {
      console.error("❌ Failed to increment coupon uses:", error);
    });
  };

  // ───────── Toggle coupon details
  const toggleCouponDetails = (couponId: string) => {
    setExpandedCouponId(expandedCouponId === couponId ? null : couponId);
  };

  // ───────── Toggle expired coupon details
  const toggleExpiredCouponDetails = (couponId: string) => {
    setExpandedExpiredCoupons((prev) =>
      prev.includes(couponId)
        ? prev.filter((id) => id !== couponId)
        : [...prev, couponId]
    );
  };

  // ───────── Filter handlers
  const handleTabChange = (tab: "all" | "promo" | "deal") => {
    if (tab === "all") {
      updateSearchParams({ couponType: null, tab: null });
    } else if (tab === "promo") {
      updateSearchParams({ couponType: "coupon", tab: "promo" });
    } else {
      updateSearchParams({ couponType: "deal", tab: "deal" });
    }
  };

  const handleCategoryToggle = (categorySlug: string) => {
    const current = selectedCategories;
    const updated = current.includes(categorySlug)
      ? current.filter((slug) => slug !== categorySlug)
      : [...current, categorySlug];
    updateSearchParams({ categories: updated.length > 0 ? updated.join(",") : null });
  };

  const handleVerifiedToggle = () => {
    updateSearchParams({ verified: verified ? null : "true" });
  };

  const handleFreeShippingToggle = () => {
    updateSearchParams({ freeShipping: freeShipping ? null : "true" });
  };

  const handleSortChange = (newSortBy: string) => {
    updateSearchParams({ sortBy: newSortBy });
  };

  const handleLimitChange = (newLimit: string) => {
    updateSearchParams({ limit: newLimit });
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(newPage));
    startTransition(() => {
      router.push(`/coupons?${params.toString()}`, { scroll: false });
    });
  };

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      updateSearchParams({ search: value || null });
    }, 500); // 500ms debounce
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ───────── Render ─────────
  return (
    <div className="px-4 md:px-10 py-10 max-w-7xl mx-auto text-gray-800">
      <h1 className="text-3xl font-bold mb-6">All Coupons</h1>

      {/* Search */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search coupons or stores..."
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (searchTimeoutRef.current) {
                  clearTimeout(searchTimeoutRef.current);
                }
                updateSearchParams({ search: localSearch || null });
              }
            }}
            className="w-full pl-10 pr-4 py-2 border rounded"
            disabled={isPending}
          />
          {isPending && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 w-4 h-4 animate-spin" />
          )}
        </div>
        <div className="w-full sm:w-64">
          <SearchableStoreSelect
            stores={initialStores}
            value={selectedStoreId}
            onValueChange={(value) => updateSearchParams({ storeId: value || null })}
            showAllOption
            allLabel="All stores"
            placeholder="Filter by store"
            searchPlaceholder="Search stores..."
            disabled={isPending}
          />
        </div>
      </div>

      {/* Top bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm text-gray-600 mb-6">
        <div>
          {isPending ? (
            "Loading..."
          ) : (
            `Showing ${coupons.length} of ${pluralize(total, "coupon")}`
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <select
              className="border rounded px-2 py-1"
              value={String(limit)}
              onChange={(e) => handleLimitChange(e.target.value)}
              disabled={isPending}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <FaSortAmountDown className="opacity-60" />
            <span>Sort by:</span>
            <select
              className="border rounded px-2 py-1"
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              disabled={isPending}
            >
              <option value="relevance">Relevance</option>
              <option value="newest">Newest</option>
              <option value="discount_desc">Discount: High → Low</option>
              <option value="discount_asc">Discount: Low → High</option>
              <option value="most_used">Most Used</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b text-sm font-medium mb-6">
        {(["all", "promo", "deal"] as const).map((tab) => (
          <button
            key={tab}
            className={`pb-2 ${
              activeTab === tab
                ? "border-b-2 border-purple-700 text-purple-700"
                : "hover:text-purple-600"
            }`}
            onClick={() => handleTabChange(tab)}
            disabled={isPending}
          >
            {tab === "all" && "All Coupons"}
            {tab === "promo" && "Promo Codes"}
            {tab === "deal" && "Deals"}
          </button>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-10">
        {/* Mobile Filters (dropdown) */}
        <div className="md:hidden mb-6">
          <details className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
            <summary className="cursor-pointer px-4 py-3 font-semibold text-gray-700">
              Filters
            </summary>
            <div className="p-4 space-y-6 text-sm">
              {/* Categories */}
              <div>
                <h3 className="font-semibold mb-3">Categories</h3>
                <ul className="space-y-2 max-h-60 overflow-auto pr-1">
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-purple-600"
                        checked={selectedCategories.length === 0}
                        onChange={() => updateSearchParams({ categories: null })}
                        disabled={isPending}
                      />
                      <span>All Categories</span>
                    </label>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat._id}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-purple-600"
                          checked={selectedCategories.includes(cat.slug)}
                          onChange={() => handleCategoryToggle(cat.slug)}
                          disabled={isPending}
                        />
                        <span>{cat.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Filters */}
              <div>
                <h3 className="font-semibold mb-3">Quick Filters</h3>
                <ul className="space-y-2">
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-purple-600"
                        checked={verified}
                        onChange={handleVerifiedToggle}
                        disabled={isPending}
                      />
                      <span>Currently Verified</span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-purple-600"
                        checked={freeShipping}
                        onChange={handleFreeShippingToggle}
                        disabled={isPending}
                      />
                      <span>Free Shipping</span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </details>
        </div>

        {/* Sidebar for desktop */}
        <aside className="hidden md:block w-full md:w-1/4 space-y-6 text-sm">
          {/* Categories */}
          <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="font-semibold mb-3">Categories</h3>
            <ul className="space-y-2 max-h-72 overflow-auto pr-1">
              <li>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-purple-600"
                    checked={selectedCategories.length === 0}
                    onChange={() => updateSearchParams({ categories: null })}
                    disabled={isPending}
                  />
                  <span>All Categories</span>
                </label>
              </li>
              {categories.map((cat) => (
                <li key={cat._id}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-purple-600"
                      checked={selectedCategories.includes(cat.slug)}
                      onChange={() => handleCategoryToggle(cat.slug)}
                      disabled={isPending}
                    />
                    <span>{cat.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Filters */}
          <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="font-semibold mb-3">Quick Filters</h3>
            <ul className="space-y-2">
              <li>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-purple-600"
                    checked={verified}
                    onChange={handleVerifiedToggle}
                    disabled={isPending}
                  />
                  <span>Currently Verified</span>
                </label>
              </li>
              <li>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-purple-600"
                    checked={freeShipping}
                    onChange={handleFreeShippingToggle}
                    disabled={isPending}
                  />
                  <span>Free Shipping</span>
                </label>
              </li>
            </ul>
          </div>

          <div className="flex justify-center">
            <AdHalfpage />
          </div>
        </aside>

        {/* Results */}
        <section className="w-full md:w-3/4 space-y-6">
          {isPending && (
            <div className="space-y-6">
              {Array.from({ length: limit }).map((_, i) => (
                <CouponSkeleton key={i} />
              ))}
            </div>
          )}
          {!isPending && coupons.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No coupons match your filters.</p>
            </div>
          )}

          {!isPending && coupons.length > 0 && (
            <div className="flex justify-center py-1">
              <AdRectangle1 />
            </div>
          )}
          {!isPending &&
            coupons.map((coupon, couponIndex) => {
              const percent = extractPercent(coupon.discount);
              const userSaved = (percent / 100) * 100;
              const avgSavings = (percent / 100) * 50;

              return (
                <React.Fragment key={coupon._id}>
                <div
                  className="flex flex-col border border-gray-200 rounded-xl bg-white shadow-md overflow-hidden"
                >
                  {/* Main Content Row */}
                  <div className="flex flex-row items-stretch">
                    {/* Left Discount Section */}
                    <div className="flex flex-col items-center justify-center min-w-[90px] md:min-w-[120px] p-3 md:p-6 text-purple-700 font-bold">
                      {(() => {
                        const discountFormat = formatDiscountDisplay(coupon.discount);
                        if (discountFormat.type === "deal") {
                          return (
                            <span className="text-lg md:text-3xl uppercase">Deal</span>
                          );
                        } else if (discountFormat.type === "text") {
                          return (
                            <>
                              <span className="text-lg md:text-3xl uppercase">{discountFormat.value}</span>
                              {discountFormat.secondLine && (
                                <span className="text-[10px] md:text-sm uppercase">{discountFormat.secondLine}</span>
                              )}
                            </>
                          );
                        } else {
                          return (
                            <>
                              <span className="text-[10px] md:text-sm uppercase">Up To</span>
                              <span className="text-lg md:text-3xl">{discountFormat.value}</span>
                              <span className="text-[10px] md:text-sm uppercase">Off</span>
                            </>
                          );
                        }
                      })()}
                    </div>

                    {/* Middle Content */}
                    <div className="flex-1 p-3 md:p-6">
                      <div className="inline-block bg-gray-100 text-gray-700 text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded mb-2 md:mb-3">
                        {coupon.couponType === "coupon" ? "Code" : "Deal"}
                      </div>
                      <h3 className="font-semibold text-sm md:text-xl text-gray-900 mb-2 md:mb-3 line-clamp-2">
                        {coupon.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1.5 md:gap-3 text-[10px] md:text-sm">
                        {coupon.verified && (
                          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                            Verified
                          </span>
                        )}
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          User saved ${userSaved.toFixed(2)}
                        </span>
                        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Avg savings: ${avgSavings.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Right Actions (desktop only) */}
                    <div className="hidden md:flex flex-col items-center justify-center min-w-[120px] md:min-w-[200px] p-3 md:p-6 border-l border-gray-100">
                      {coupon.couponType === "coupon" ? (
                        <CouponRevealButton
                          onClick={() => handleOpenCouponNewTab(coupon)}
                          aria-label={`Show code for ${coupon.title}`}
                          couponCode={coupon.couponCode}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenCouponNewTab(coupon)}
                          className="inline-flex h-10 w-[10.9375rem] cursor-pointer items-center justify-center rounded-full bg-purple-600 px-4 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-purple-700 whitespace-nowrap"
                        >
                          Get Deal
                        </button>
                      )}

                      <button
                        onClick={() => toggleCouponDetails(coupon._id)}
                        className="text-xs md:text-sm text-gray-500 mt-2 md:mt-3 hover:underline"
                      >
                        {expandedCouponId === coupon._id ? "Hide Details -" : "See Details +"}
                      </button>

                      <p className="mt-1 text-[11px] md:text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">
                        Used {coupon.uses} times
                      </p>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="flex flex-col md:hidden gap-2 p-3">
                    {coupon.couponType === "coupon" ? (
                      <CouponRevealButton
                        onClick={() => handleOpenCouponNewTab(coupon)}
                        aria-label={`Show code for ${coupon.title}`}
                        couponCode={coupon.couponCode}
                        mobile
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenCouponNewTab(coupon)}
                        className="inline-flex h-10 w-full cursor-pointer items-center justify-center rounded-full bg-purple-600 px-4 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-purple-700 whitespace-nowrap"
                      >
                        Get Deal
                      </button>
                    )}

                    <div className="flex justify-between items-center mt-2 text-[11px] text-gray-700">
                      <span
                        className="text-purple-700 font-medium hover:underline cursor-pointer"
                        onClick={() => toggleCouponDetails(coupon._id)}
                      >
                        {expandedCouponId === coupon._id ? "Hide Details -" : "See Details +"}
                      </span>
                      <span className="bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                        Used {coupon.uses} times
                      </span>
                    </div>
                  </div>

                  {/* Expanded Description */}
                  {expandedCouponId === coupon._id && coupon.description && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50 text-sm text-gray-700">
                      {coupon.description}
                    </div>
                  )}
                </div>
                <div className="flex justify-center py-1">
                  {couponIndex % 2 === 0 ? <AdRectangle1 /> : <AdRectangle2 />}
                </div>
                </React.Fragment>
              );
            })}

          {/* Pagination */}
          {!isPending && totalPages > 1 && (() => {
            const visiblePages: Array<number | "ellipsis"> = [];

            if (totalPages <= 8) {
              for (let i = 1; i <= totalPages; i++) visiblePages.push(i);
            } else {
              const pagesAroundCurrent = 3;
              const start = Math.max(1, page - pagesAroundCurrent);
              const end = Math.min(totalPages, page + pagesAroundCurrent);

              if (start > 1) {
                visiblePages.push(1);
                if (start > 2) visiblePages.push("ellipsis");
              }

              for (let i = start; i <= end; i++) visiblePages.push(i);

              if (end < totalPages) {
                if (end < totalPages - 1) visiblePages.push("ellipsis");
                visiblePages.push(totalPages);
              }
            }

            return (
              <div className="flex items-center justify-center gap-2 pt-4 flex-wrap">
                <button
                  disabled={page === 1 || isPending}
                  onClick={() => handlePageChange(page - 1)}
                  className={`px-3 py-1.5 rounded border text-sm ${
                    page === 1
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-purple-700 border-purple-200 hover:bg-purple-50"
                  }`}
                >
                  Prev
                </button>

                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {visiblePages.map((pageNum, idx) => {
                    if (pageNum === "ellipsis") {
                      return (
                        <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">
                          ...
                        </span>
                      );
                    }

                    const isActive = pageNum === page;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`h-9 min-w-[2.25rem] rounded border text-sm ${
                          isActive
                            ? "bg-purple-700 text-white border-purple-700"
                            : "border-gray-200 hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  disabled={page === totalPages || isPending}
                  onClick={() => handlePageChange(page + 1)}
                  className={`px-3 py-1.5 rounded border text-sm ${
                    page === totalPages
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-purple-700 border-purple-200 hover:bg-purple-50"
                  }`}
                >
                  Next
                </button>
              </div>
            );
          })()}
        </section>
      </div>

      {/* 🟥 Expired Coupons Section */}
      {expiredCoupons.length > 0 && (
        <div className="w-full max-w-7xl mx-auto mt-12 text-center">
          {/* Section Heading */}
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-2">
            Expired Coupon Codes
          </h2>
          <p className="text-black mb-6">
            Check out these expired coupons — sometimes they still work!
          </p>

          {/* Container Box */}
          <div className="w-full max-w-7xl mx-auto rounded-xl p-4 md:p-6 bg-gray-50 shadow-sm space-y-6 text-left">
            {expiredCoupons.map((coupon) => (
              <div
                key={coupon._id}
                className="max-w-4xl w-full mx-auto flex flex-col border border-black rounded-xl bg-white shadow-md overflow-hidden opacity-75"
              >
                {/* Main Row */}
                <div className="flex flex-row items-stretch">
                  {/* Left: Discount */}
                  <div className="flex flex-col items-center justify-center min-w-[90px] md:min-w-[120px] p-3 md:p-6 text-black font-bold">
                    {(() => {
                      const discountFormat = formatDiscountDisplay(coupon.discount);
                      if (discountFormat.type === "deal") {
                        return (
                          <span className="text-lg md:text-3xl uppercase">Deal</span>
                        );
                      } else if (discountFormat.type === "text") {
                        return (
                          <>
                            <span className="text-lg md:text-3xl uppercase">{discountFormat.value}</span>
                            {discountFormat.secondLine && (
                              <span className="text-[10px] md:text-sm uppercase">{discountFormat.secondLine}</span>
                            )}
                          </>
                        );
                      } else {
                        return (
                          <>
                            <span className="text-[10px] md:text-sm uppercase">Up To</span>
                            <span className="text-lg md:text-3xl">{discountFormat.value}</span>
                            <span className="text-[10px] md:text-sm uppercase">Off</span>
                          </>
                        );
                      }
                    })()}
                  </div>

                  {/* Middle: Content */}
                  <div className="flex-1 p-3 md:p-6">
                    <div className="inline-block bg-gray-100 text-black text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded mb-2 md:mb-3">
                      {coupon.couponType === "coupon" ? "Code" : "Deal"}
                    </div>
                    <h3 className="font-semibold text-sm md:text-xl text-black mb-2 md:mb-3 line-clamp-2">
                      {coupon.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-3 text-[10px] md:text-sm">
                      <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">
                        Expired
                      </span>
                      {coupon.expirationDate && (
                        <span className="bg-gray-100 text-black px-1.5 py-0.5 rounded">
                          {new Date(coupon.expirationDate).toISOString().split("T")[0]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions (Desktop) */}
                  <div className="hidden md:flex flex-col items-center justify-center min-w-[120px] md:min-w-[200px] p-3 md:p-6 border-l border-black">
                    <button
                      onClick={() => handleOpenCouponNewTab(coupon)}
                      className="w-36 h-11 bg-black hover:bg-gray-500 text-white font-semibold text-sm px-4 py-2 rounded-full"
                    >
                      Expired
                    </button>

                    <button
                      onClick={() => toggleExpiredCouponDetails(coupon._id)}
                      className="text-xs md:text-sm text-purple-700 mt-2 md:mt-3 font-medium hover:underline"
                    >
                      {expandedExpiredCoupons.includes(coupon._id)
                        ? "Hide Details -"
                        : "See Details +"}
                    </button>
                    <p className="mt-1 text-[11px] md:text-xs bg-gray-100 text-black px-2 py-0.5 rounded-full font-medium">
                      Used {coupon.uses} times
                    </p>
                  </div>
                </div>

                {/* Mobile Actions */}
                <div className="flex flex-col md:hidden gap-2 p-3">
                  <button
                    onClick={() => handleOpenCouponNewTab(coupon)}
                    className="w-full bg-black hover:bg-gray-500 text-white font-semibold text-sm px-4 py-2 rounded-full cursor-not-allowed"
                  >
                    Expired
                  </button>
                  <div className="flex justify-between items-center mt-2 text-[11px] text-black">
                    <span
                      className="text-purple-700 font-medium hover:underline cursor-pointer"
                      onClick={() => toggleExpiredCouponDetails(coupon._id)}
                    >
                      {expandedExpiredCoupons.includes(coupon._id)
                        ? "Hide Details -"
                        : "See Details +"}
                    </span>
                    <span className="bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                      Used {coupon.uses} times
                    </span>
                  </div>
                </div>

                {/* Expanded Description */}
                {expandedExpiredCoupons.includes(coupon._id) && coupon.description && (
                  <div className="border-t border-black p-4 bg-gray-50 text-sm text-black">
                    {coupon.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <CouponModal
        storeName={selectedCoupon?.store?.name}
        storeSlug={selectedCoupon?.store?.slug}
        storeImageUrl={selectedCoupon?.store?.image}
        title={selectedCoupon?.title}
        discount={selectedCoupon?.discount}
        code={selectedCoupon?.couponCode}
        redeemUrl={selectedCoupon?.couponUrl}
        description={selectedCoupon?.description}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
